import { GetQueryParams, GetQueryResponse } from '../../../types/api.type';
import { Lesson } from '../../../types/lesson.type';
import { instructor_roles } from '../../../utils/consts';
import { sfcApi } from '../../api';

interface InstructorLessonsResponse {
  label?: string;
  lessons: Lesson[];
}

interface LessonsCountResponse {
  lessonsCount: Array<{
    count_all: number;
    lesson_date: string;
  }>;
}

interface GetLessonCountQuery {
  start?: string;
  end?: string;
  instructor_id?: number;
  location_id?: number;
  territory_id?: number;
}

export const lessonApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getLessons: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `lessons${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      })
    }),

    getInstructorLessons: builder.query<InstructorLessonsResponse[], GetQueryParams | void>({
      async queryFn(arg, _queryApi, _extraOptions, baseQuery) {
        try {
          const url = `lessons${arg?.scope ? `?scope=${arg.scope}` : ''}`;

          // Validate we have required filters
          if (!arg?.data?.filterBy || arg.data.filterBy.length < 2) {
            throw new Error('Insufficient filters provided');
          }

          // Always include the first two filters (date range)
          const baseFilters = [arg.data.filterBy[0], arg.data.filterBy[1]];

          // Include optional location/territory filters if they exist (exclude instructor filters)
          const optionalFilters = arg.data.filterBy
            .slice(2, 4)
            .filter((f) => f && Number(f.value) && f.field !== 'instructed_id');

          // Get instructor ID from the last filter
          const instructorFilter = arg.data.filterBy[arg.data.filterBy.length - 1];
          if (!instructorFilter || !instructorFilter.value) {
            throw new Error('Instructor filter missing');
          }

          const requests = [
            {
              url,
              method: 'POST',
              body: {
                ...arg?.data,
                filterBy: [
                  ...baseFilters,
                  ...optionalFilters,
                  {
                    field: 'instructed_id',
                    operator: 'equals',
                    value: instructorFilter.value
                  }
                ]
              }
            }
          ];

          const responses = await Promise.all(requests.map((r) => baseQuery(r)));

          const results: InstructorLessonsResponse[] = responses.map((res, index) => {
            if (res.error) throw res.error;
            const data = res.data as GetQueryResponse;
            return {
              label: instructor_roles[index],
              lessons: data.results as Lesson[]
            };
          });

          // Return proper RTK Query response format
          return { data: results };
        } catch (error) {
          // Return proper RTK Query error format
          if (error instanceof Error) {
            return { error: { status: 'CUSTOM_ERROR', error: error.message } };
          }
          return { error: { status: 'CUSTOM_ERROR', error: 'Unknown error' } };
        }
      }
    }),

    // NEW ENDPOINT - Calendar lessons using POST /lessons/calendar
    getCalendarLessons: builder.query<Lesson[], GetQueryParams | void>({
      query: (params) => ({
        url: 'lessons/calendar',
        method: 'POST',
        body: params?.data
      })
    }),

    getLessonsCount: builder.query<LessonsCountResponse, GetLessonCountQuery>({
      query: (data) => ({
        url: `lessons/lessonsCount`,
        method: 'POST',
        body: data
      })
    }),
    getChildrenLessonPlans: builder.query<Lesson[], { data: any } | void>({
      query: (params) => ({
        url: `lessons/children-lessons`,
        method: 'POST',
        body: params?.data
      }),
      forceRefetch: () => {
        return true;
      }
    })
  })
});

export const {
  useGetLessonsQuery,
  useGetInstructorLessonsQuery,
  useGetCalendarLessonsQuery,
  useGetLessonsCountQuery,
  useGetChildrenLessonPlansQuery
} = lessonApi;
